import React, { useEffect, useState } from "react";
import { CalendarCog, CalendarClock, Plus, X, Check, Ban, Loader2, Save, MessageSquareWarning, MessageCircle, Briefcase, MailCheck } from "lucide-react";
import { api, adminHeaders, waLink } from "@/lib/api";
import { toast } from "sonner";

// Python weekday(): Mon=0 .. Sun=6. Display order Sun -> Sat.
const WEEKDAYS = [
  { val: 6, label: "Sun" },
  { val: 0, label: "Mon" },
  { val: 1, label: "Tue" },
  { val: 2, label: "Wed" },
  { val: 3, label: "Thu" },
  { val: 4, label: "Fri" },
  { val: 5, label: "Sat" },
];

// Internal status -> customer-facing label + colour
const BOOKING_STATUS_META = {
  Requested: { label: "Pending", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  Approved: { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  "Changes Requested": { label: "Changes Requested", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  Declined: { label: "Declined", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  Completed: { label: "Completed", cls: "bg-green-600/15 text-green-300 border-green-600/30" },
};
const FILTERS = ["All", "Requested", "Approved", "Changes Requested", "Declined", "Completed"];

const inputCls = "w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";

function AvailabilitySettings() {
  const [cfg, setCfg] = useState(null);
  const [slotsText, setSlotsText] = useState("");
  const [newBlackout, setNewBlackout] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/availability", { headers: adminHeaders() })
      .then(({ data }) => { setCfg(data); setSlotsText((data.slots || []).join("\n")); })
      .catch(() => toast.error("Couldn't load availability settings"));
  }, []);

  if (!cfg) return <div className="ascendia-card p-6 flex items-center gap-2 text-muted-c"><Loader2 className="h-4 w-4 animate-spin" /> Loading settings…</div>;

  const toggleDay = (val) => {
    const days = cfg.working_days.includes(val) ? cfg.working_days.filter((d) => d !== val) : [...cfg.working_days, val];
    setCfg({ ...cfg, working_days: days });
  };
  const addBlackout = () => {
    if (!newBlackout) return;
    if (cfg.blackout_dates.includes(newBlackout)) { toast.info("Date already blocked"); return; }
    setCfg({ ...cfg, blackout_dates: [...cfg.blackout_dates, newBlackout].sort() });
    setNewBlackout("");
  };
  const removeBlackout = (d) => setCfg({ ...cfg, blackout_dates: cfg.blackout_dates.filter((x) => x !== d) });

  const save = async () => {
    setSaving(true);
    const slots = slotsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!slots.length) { toast.error("Please define at least one time window"); setSaving(false); return; }
    try {
      const { data } = await api.put("/admin/availability", {
        working_days: cfg.working_days,
        slots,
        blackout_dates: cfg.blackout_dates,
        capacity_per_slot: Number(cfg.capacity_per_slot) || 1,
        lead_time_days: Number(cfg.lead_time_days) || 0,
        horizon_days: Number(cfg.horizon_days) || 30,
        booking_enabled: cfg.booking_enabled,
        note: cfg.note,
      }, { headers: adminHeaders() });
      setCfg(data); setSlotsText((data.slots || []).join("\n"));
      toast.success("Availability settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="ascendia-card p-6" data-testid="admin-availability-settings">
      <div className="flex items-center gap-2 mb-5"><CalendarCog className="h-5 w-5 text-gold-soft" /><h3 className="font-display text-lg font-semibold text-white">Availability settings</h3></div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-secondary mb-2">Working days</label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => {
              const on = cfg.working_days.includes(d.val);
              return (
                <button key={d.val} type="button" onClick={() => toggleDay(d.val)} data-testid={`admin-workday-${d.label.toLowerCase()}`}
                  className={`rounded-lg px-3 py-2 text-sm border transition-colors ${on ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm text-secondary mb-2">Time windows (one per line)</label>
          <textarea rows={4} value={slotsText} onChange={(e) => setSlotsText(e.target.value)} data-testid="admin-slots-textarea"
            className={inputCls} placeholder={"Morning (08:00–12:00)\nAfternoon (12:00–16:00)"} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-sm text-secondary mb-1.5">Capacity / window</label><input type="number" min="1" value={cfg.capacity_per_slot} onChange={(e) => setCfg({ ...cfg, capacity_per_slot: e.target.value })} className={inputCls} data-testid="admin-capacity-input" /></div>
          <div><label className="block text-sm text-secondary mb-1.5">Lead time (days)</label><input type="number" min="0" value={cfg.lead_time_days} onChange={(e) => setCfg({ ...cfg, lead_time_days: e.target.value })} className={inputCls} data-testid="admin-leadtime-input" /></div>
          <div><label className="block text-sm text-secondary mb-1.5">Horizon (days)</label><input type="number" min="1" value={cfg.horizon_days} onChange={(e) => setCfg({ ...cfg, horizon_days: e.target.value })} className={inputCls} data-testid="admin-horizon-input" /></div>
        </div>

        <div>
          <label className="block text-sm text-secondary mb-2">Blackout dates</label>
          <div className="flex gap-2">
            <input type="date" value={newBlackout} onChange={(e) => setNewBlackout(e.target.value)} className={inputCls} data-testid="admin-blackout-input" />
            <button type="button" onClick={addBlackout} className="inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-subtle)] px-3 text-sm text-white hover:bg-white/5"><Plus className="h-4 w-4" /></button>
          </div>
          {cfg.blackout_dates.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {cfg.blackout_dates.map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-subtle)] bg-black/30 px-3 py-1 text-xs text-white">
                  {d}<button onClick={() => removeBlackout(d)} className="text-muted-c hover:text-white"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm text-secondary mb-1.5">Customer-facing note</label>
        <textarea rows={2} value={cfg.note} onChange={(e) => setCfg({ ...cfg, note: e.target.value })} className={inputCls} data-testid="admin-note-textarea" />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={cfg.booking_enabled} onChange={(e) => setCfg({ ...cfg, booking_enabled: e.target.checked })} className="h-4 w-4 accent-[#C8A14A]" data-testid="admin-booking-enabled-toggle" />
          <span className="text-sm text-secondary">Online time requests {cfg.booking_enabled ? "enabled" : "disabled"}</span>
        </label>
        <button onClick={save} disabled={saving} data-testid="admin-save-availability" className="inline-flex items-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </button>
      </div>
    </div>
  );
}

function BookingRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [changesFor, setChangesFor] = useState(null); // booking pending "request changes" note
  const [noteText, setNoteText] = useState("");
  const [convertFor, setConvertFor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/booking-requests", { headers: adminHeaders() })
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Couldn't load booking requests"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status, note = "") => {
    try {
      const { data } = await api.patch(`/admin/booking-requests/${id}/status`, { status, note }, { headers: adminHeaders() });
      const meta = BOOKING_STATUS_META[status];
      toast.success(`Marked ${meta?.label || status}`);
      if (data.email_result && data.email_result.sent === false && data.email_result.reason && data.email_result.reason.includes("403")) {
        toast.info("Email not sent — verify the SendGrid sender to enable customer emails.");
      }
      load();
    } catch { toast.error("Update failed"); }
  };

  const submitChanges = async () => {
    if (!changesFor) return;
    await setStatus(changesFor.id, "Changes Requested", noteText);
    setChangesFor(null); setNoteText("");
  };

  const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const shown = filter === "All" ? items : items.filter((b) => b.status === filter);

  const waFor = (b) => {
    const first = (b.name || "there").split(" ")[0];
    const dateStr = (() => { try { return fmtDate(b.date); } catch { return b.date; } })();
    const meta = BOOKING_STATUS_META[b.status];
    let msg;
    if (b.status === "Approved") msg = `Hi ${first}, this is Ascendia Detail. Your booking ${b.ref} is confirmed for ${dateStr}, ${b.slot}${b.service ? ` (${b.service})` : ""}. Let us know if you have any questions!`;
    else if (b.status === "Changes Requested") msg = `Hi ${first}, this is Ascendia Detail regarding your request ${b.ref} for ${dateStr}, ${b.slot}. We'd like to adjust a small detail before confirming — when is a good time to talk?`;
    else if (b.status === "Declined") msg = `Hi ${first}, this is Ascendia Detail regarding your request ${b.ref}. Unfortunately we couldn't confirm that window — we'd love to find another option that works for you.`;
    else msg = `Hi ${first}, this is Ascendia Detail. We received your request ${b.ref} for ${dateStr}, ${b.slot}${b.service ? ` (${b.service})` : ""} and will confirm shortly.`;
    return waLink(b.phone, msg);
  };

  return (
    <div data-testid="admin-booking-requests">
      <div className="flex items-center gap-2 mb-4"><CalendarClock className="h-5 w-5 text-gold-soft" /><h3 className="font-display text-lg font-semibold text-white">Booking requests</h3></div>
      <div className="flex gap-2 flex-wrap mb-4">
        {FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs border ${filter === s ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>{s === "All" ? "All" : (BOOKING_STATUS_META[s]?.label || s)}</button>
        ))}
      </div>
      {loading ? (
        <div className="ascendia-card p-6 flex items-center gap-2 text-muted-c"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : shown.length === 0 ? (
        <p className="text-muted-c text-sm">No booking requests in this view yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((b) => {
            const meta = BOOKING_STATUS_META[b.status] || { label: b.status, cls: "border-white/20 text-white/70" };
            const emailed = (b.email_sent_kinds || []).length > 0;
            return (
            <div key={b.id} className="ascendia-card p-5 flex flex-col" data-testid="booking-request-card">
              <div className="flex justify-between items-start">
                <span className="font-semibold text-white">{b.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid="booking-status-badge">{meta.label}</span>
              </div>
              <p className="text-xs font-mono text-gold-soft mt-1">{b.ref}</p>
              <p className="mt-2 text-sm text-white">{fmtDate(b.date)}</p>
              <p className="text-xs text-gold-soft">{b.slot}</p>
              <p className="mt-2 text-sm text-secondary">{b.service || "—"}</p>
              {(b.address || b.city) && <p className="text-xs text-muted-c mt-1">{[b.address, b.city].filter(Boolean).join(", ")}</p>}
              <p className="text-xs text-muted-c mt-1">{b.phone}{b.email ? ` · ${b.email}` : ""}</p>
              {b.notes && <p className="text-xs text-secondary mt-2 border-l-2 border-gold/40 pl-2">{b.notes}</p>}
              {b.decision_note && <p className="text-xs text-sky-300 mt-2 border-l-2 border-sky-500/40 pl-2">Note to customer: {b.decision_note}</p>}
              {b.job_ref && <p className="text-[10px] text-emerald-300 mt-2 inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> Job {b.job_ref}</p>}
              {emailed && <p className="text-[10px] text-muted-c mt-1 inline-flex items-center gap-1"><MailCheck className="h-3 w-3 text-emerald-400" /> Customer notified</p>}
              {b.email && (b.email_confirmed === false) && null}

              <div className="mt-auto pt-4 space-y-2">
                {b.status === "Requested" && (
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => setStatus(b.id, "Approved")} data-testid="booking-approve-button" className="inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 py-2 text-xs text-emerald-300 hover:bg-emerald-500/25"><Check className="h-3.5 w-3.5" /> Approve</button>
                    <button onClick={() => { setChangesFor(b); setNoteText(""); }} data-testid="booking-changes-button" className="inline-flex items-center justify-center gap-1 rounded-lg bg-sky-500/15 border border-sky-500/30 py-2 text-xs text-sky-300 hover:bg-sky-500/25"><MessageSquareWarning className="h-3.5 w-3.5" /> Changes</button>
                    <button onClick={() => setStatus(b.id, "Declined")} data-testid="booking-decline-button" className="inline-flex items-center justify-center gap-1 rounded-lg bg-red-500/15 border border-red-500/30 py-2 text-xs text-red-300 hover:bg-red-500/25"><Ban className="h-3.5 w-3.5" /> Decline</button>
                  </div>
                )}
                {b.status === "Approved" && !b.job_ref && (
                  <button onClick={() => setConvertFor(b)} data-testid="booking-convert-button" className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-gold py-2 text-xs font-semibold text-black hover:bg-gold-soft"><Briefcase className="h-3.5 w-3.5" /> Create job from booking</button>
                )}
                <a href={waFor(b)} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp-button" className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] py-2 text-xs text-white hover:bg-white/5"><MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> Contact on WhatsApp</a>
              </div>
            </div>
          );})}
        </div>
      )}

      {/* Request changes note modal */}
      {changesFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setChangesFor(null)}>
          <div className="ascendia-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="booking-changes-modal">
            <div className="flex justify-between items-center mb-3"><h4 className="font-display text-lg font-semibold text-white">Request changes</h4><button onClick={() => setChangesFor(null)} className="text-muted-c hover:text-white"><X className="h-5 w-5" /></button></div>
            <p className="text-sm text-secondary mb-3">Tell {changesFor.name.split(" ")[0]} what you'd like to adjust. This note is included in the email we send them.</p>
            <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="booking-changes-note" className={inputCls} placeholder="e.g. Could we move this to a morning window? The afternoon is fully booked." />
            <button onClick={submitChanges} data-testid="booking-changes-submit" className="mt-4 w-full rounded-lg bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">Send changes request</button>
          </div>
        </div>
      )}

      {convertFor && <ConvertBookingModal booking={convertFor} onClose={() => setConvertFor(null)} onDone={() => { setConvertFor(null); load(); }} />}
    </div>
  );
}

function ConvertBookingModal({ booking, onClose, onDone }) {
  const [f, setF] = useState({ quote_amount: "", deposit_amount: "", balance_amount: "", appointment_time: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/booking-requests/${booking.id}/convert-to-job`, {
        quote_amount: f.quote_amount ? Number(f.quote_amount) : null,
        deposit_amount: f.deposit_amount ? Number(f.deposit_amount) : null,
        balance_amount: f.balance_amount ? Number(f.balance_amount) : null,
        appointment_time: f.appointment_time || "",
      }, { headers: adminHeaders() });
      toast.success("Job created from booking");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create job"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="ascendia-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="booking-convert-modal">
        <div className="flex justify-between items-center mb-3"><h4 className="font-display text-lg font-semibold text-white">Create job from booking</h4><button onClick={onClose} className="text-muted-c hover:text-white"><X className="h-5 w-5" /></button></div>
        <p className="text-sm text-secondary mb-4">{booking.name} · {booking.service || "Service TBC"} · {booking.date} {booking.slot}</p>
        <div className="space-y-3">
          <input className={inputCls} placeholder="Confirmed time (e.g. 09:00)" value={f.appointment_time} onChange={upd("appointment_time")} data-testid="convert-time" />
          <div className="grid grid-cols-3 gap-2">
            <input className={inputCls} placeholder="Quote ₪" value={f.quote_amount} onChange={upd("quote_amount")} data-testid="convert-quote" />
            <input className={inputCls} placeholder="Deposit ₪" value={f.deposit_amount} onChange={upd("deposit_amount")} />
            <input className={inputCls} placeholder="Balance ₪" value={f.balance_amount} onChange={upd("balance_amount")} />
          </div>
          <button onClick={submit} disabled={busy} data-testid="convert-submit" className="w-full rounded-lg bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">{busy ? "Creating…" : "Create job"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminScheduling() {
  return (
    <div className="space-y-8">
      <AvailabilitySettings />
      <BookingRequests />
    </div>
  );
}
